import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Play, ShieldAlert, SlidersHorizontal } from 'lucide-react';
import Header from '../landing/Header';
import Footer from '../landing/Footer';
import ListingCard from '../listings/ListingCard';
import PlayerModal from '../listings/PlayerModal';
import ReelsView from '../listings/ReelsView';
import { Toggle, canHover, isVideo, usePref } from '../listings/embed';
import { DEAL_OPTIONS, PLATFORM_LABEL, RENT_BUDGETS, SALE_BUDGETS, TYPE_OPTIONS, formatFcfa, formatMillions } from '../listings/labels';
import { track } from '../listings/track';
import { COUNTRIES } from '../listings/countries';
import apiService from '../../api';

const EMPTY_FILTERS = { deal: '', new: '', q: '', city: '', type: '', price_max: '', platform: '', agent: '', sort: 'new' };
const PLATFORMS = [['', 'Tous'], ['tiktok', 'TikTok'], ['facebook', 'Facebook']];

function ListingsPage() {
  const { country } = useParams(); // checked by CountryGate
  const countryName = COUNTRIES[country];
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [search, setSearch] = useState('');
  const [facets, setFacets] = useState(null);
  const [listings, setListings] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [hoverPlay, setHoverPlay] = usePref('hoverplay', true);
  const [player, setPlayer] = useState(null);
  const [reelsOpen, setReelsOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false); // phones only; always shown from md up

  useEffect(() => {
    track('feed_view');
    apiService.get('/api/v1/listings/facets/', { params: { country } }).then(setFacets).catch(() => setFacets(null));
  }, [country]);

  // Search box: wait until the user stops typing.
  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== filters.q) set('q', search.trim());
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    const params = Object.fromEntries(Object.entries({ ...filters, country, page }).filter(([, v]) => v !== ''));
    apiService
      .get('/api/v1/listings/', { params })
      .then((data) => {
        if (cancelled) return;
        setListings((prev) => (page === 1 ? data.results : [...prev, ...data.results]));
        setCount(data.count);
        setHasMore(Boolean(data.next));
      })
      .catch(() => !cancelled && setError(true))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [filters, page, country]);

  const set = (key, value) => {
    setPage(1);
    setFilters((f) => ({ ...f, [key]: value }));
  };
  const update = (key) => (e) => set(key, e.target.value);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) setPage((p) => p + 1);
  }, [loading, hasMore]);
  const closePlayer = useCallback(() => setPlayer(null), []);
  const closeReels = useCallback(() => setReelsOpen(false), []);

  const videos = listings.filter(isVideo);
  // Filters set beyond the defaults (the deal switch and the search box are always visible).
  const activeFilters = ['city', 'type', 'price_max', 'agent', 'platform', 'new'].filter((k) => filters[k]).length
    + (filters.sort !== 'new' ? 1 : 0);
  const clearFilters = () => {
    setPage(1);
    setFilters((f) => ({ ...EMPTY_FILTERS, deal: f.deal, q: f.q }));
  };
  const countLabel = `${loading && page === 1 ? 'Chargement…' : `${count} annonce${count > 1 ? 's' : ''}`}${facets ? ` · ${facets.days} derniers jours` : ''}`;
  const agents = (facets?.agents || []).filter((a) => !filters.platform || a.platform === filters.platform);
  const selectClass = 'w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Helmet>
        <title>{`Immobilier au ${countryName} : location, vente, terrains - Recouvro`}</title>
        <meta
          name="description"
          content={`Toutes les annonces immobilières publiées par les agents au ${countryName} sur TikTok et Facebook : logements à louer, maisons et terrains à vendre. Contactez directement l'agent.`}
        />
      </Helmet>

      <Header />

      <main className="flex-grow">
        <section className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-5 sm:pt-8">
          <h1 className="text-xl sm:text-3xl font-bold text-gray-900">Immobilier à louer et à vendre au {countryName}</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">
            Logements, maisons, terrains : les annonces publiées par les agents sur TikTok et Facebook, au même endroit.
            <span className="hidden sm:inline"> Chaque annonce renvoie vers l'agent qui l'a publiée : vous le contactez directement.</span>
          </p>
        </section>

        <section className="sticky top-0 z-30 bg-gray-50/95 backdrop-blur border-b border-gray-200 mt-4">
          <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-2 md:py-3 flex flex-col gap-2 md:gap-2.5">
            <div className="grid grid-cols-3 sm:inline-flex sm:self-start border border-gray-300 rounded-lg overflow-hidden bg-white" role="group" aria-label="À louer ou à vendre">
              {DEAL_OPTIONS.map(([v, label]) => {
                const n = v ? facets?.deals?.[v] : facets?.total;
                return (
                  <button
                    key={v || 'all'}
                    type="button"
                    aria-pressed={filters.deal === v}
                    onClick={() => { setPage(1); setFilters((f) => ({ ...f, deal: v, price_max: '' })); }}
                    className={`text-sm px-4 py-2 ${filters.deal === v ? 'bg-blue-600 text-white font-semibold' : 'text-gray-600'}`}
                  >
                    {label}{n !== undefined ? ` (${n})` : ''}
                  </button>
                );
              })}
            </div>
            {/* Phones: search, "Filtres" and video mode on one row; the rest opens on demand. */}
            <div className="flex gap-2">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Quartier, chambre salon, parcelle, 45000…"
                list="listing-areas"
                className={`${selectClass} flex-1 min-w-0`}
                aria-label="Rechercher"
              />
              <button
                type="button"
                onClick={() => setShowFilters((v) => !v)}
                aria-expanded={showFilters}
                className={`md:hidden shrink-0 inline-flex items-center gap-1.5 border rounded-lg px-3 text-sm font-medium ${activeFilters ? 'border-blue-500 text-blue-700 bg-blue-50' : 'border-gray-300 text-gray-700 bg-white'}`}
              >
                <SlidersHorizontal size={16} /> Filtres{activeFilters ? ` (${activeFilters})` : ''}
              </button>
              <button
                type="button"
                onClick={() => setReelsOpen(true)}
                disabled={!videos.length}
                aria-label="Mode vidéo"
                className="md:hidden shrink-0 inline-flex items-center justify-center w-10 text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg disabled:opacity-40"
              >
                <Play size={16} fill="currentColor" />
              </button>
            </div>
            <datalist id="listing-areas">
              {(facets?.areas || []).map((a) => <option key={`${a.name}-${a.city}`} value={a.name} />)}
            </datalist>

            <div className={`${showFilters ? 'flex' : 'hidden'} md:flex flex-col gap-2.5`}>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
              <select value={filters.city} onChange={update('city')} className={selectClass} aria-label="Ville">
                <option value="">Toutes les villes</option>
                {(facets?.cities || []).map((c) => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
              </select>
              <select value={filters.type} onChange={update('type')} className={selectClass} aria-label="Type de bien">
                <option value="">Tous les types</option>
                {TYPE_OPTIONS.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
              </select>
              <select value={filters.price_max} onChange={update('price_max')} className={selectClass} aria-label="Budget">
                <option value="">Tous les budgets</option>
                {filters.deal === 'sale'
                  ? SALE_BUDGETS.map((v) => <option key={v} value={v}>Jusqu'à {formatMillions(v)}</option>)
                  : RENT_BUDGETS.map((v) => <option key={v} value={v}>Loyer jusqu'à {formatFcfa(v)} / mois</option>)}
              </select>
              <select value={filters.agent} onChange={update('agent')} className={selectClass} aria-label="Agent">
                <option value="">Tous les agents</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{PLATFORM_LABEL[a.platform]} · {a.name} ({a.count})</option>
                ))}
              </select>
              <select value={filters.sort} onChange={update('sort')} className={`${selectClass} col-span-2 md:col-span-1`} aria-label="Trier">
                <option value="new">Plus récentes</option>
                <option value="old">Plus anciennes</option>
                <option value="cheap">{filters.deal === 'sale' ? 'Prix le plus bas' : 'Loyer le plus bas'}</option>
              </select>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden bg-white" role="group" aria-label="Plateforme">
                {PLATFORMS.map(([v, label]) => (
                  <button
                    key={v || 'all'}
                    type="button"
                    aria-pressed={filters.platform === v}
                    onClick={() => { set('platform', v); set('agent', ''); }}
                    className={`text-sm px-3 py-2 ${filters.platform === v ? 'bg-blue-50 text-blue-700 font-semibold' : 'text-gray-500'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <Toggle on={filters.new === '1'} onChange={(on) => set('new', on ? '1' : '')}>
                Nouvelle construction{facets?.new_build !== undefined ? ` (${facets.new_build})` : ''}
              </Toggle>
              {canHover && <Toggle on={hoverPlay} onChange={setHoverPlay}>Lecture au survol</Toggle>}
              {activeFilters > 0 && (
                <button type="button" onClick={clearFilters} className="text-sm text-gray-500 underline">Effacer</button>
              )}
              <button
                type="button"
                onClick={() => setReelsOpen(true)}
                disabled={!videos.length}
                className="hidden md:inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg px-4 py-2 disabled:opacity-40"
              >
                <Play size={14} fill="currentColor" /> Mode vidéo ({videos.length}{hasMore ? '+' : ''})
              </button>
              <span className="hidden md:inline text-sm text-gray-500 md:ml-auto">{countLabel}</span>
            </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-3 md:pt-4 pb-12">
          <p className="md:hidden text-sm text-gray-500 mb-2">{countLabel}</p>
          {error && (
            <p className="text-center text-red-600 py-12">Impossible de charger les annonces. Réessayez dans un instant.</p>
          )}
          {!error && !loading && listings.length === 0 && (
            <p className="text-center text-gray-500 py-12">Aucune annonce ne correspond à ces critères.</p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {listings.map((l) => <ListingCard key={l.id} listing={l} hoverPlay={hoverPlay} onOpen={setPlayer} />)}
          </div>

          {hasMore && (
            <div className="text-center mt-8">
              <button
                type="button"
                onClick={loadMore}
                disabled={loading}
                className="bg-white border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-100 disabled:opacity-50"
              >
                {loading ? 'Chargement…' : "Voir plus d'annonces"}
              </button>
            </div>
          )}

          <div className="mt-10 bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-sm text-amber-900">
            <ShieldAlert size={20} className="shrink-0 mt-0.5" />
            <p>
              Recouvro n'est pas une agence et ne prend aucune commission : les annonces appartiennent aux agents qui les ont publiées.
              Ne payez jamais de frais avant d'avoir visité le bien et rencontré l'agent. Les prix et quartiers affichés sont lus
              automatiquement dans l'annonce : vérifiez-les auprès de l'agent.
            </p>
          </div>
        </section>
      </main>

      <Footer />

      {player && <PlayerModal listing={player} onClose={closePlayer} />}
      {reelsOpen && (
        <ReelsView listings={videos} onClose={closeReels} hasMore={hasMore} onLoadMore={loadMore} />
      )}
    </div>
  );
}

export default ListingsPage;
